import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiPost } from '../../utils/api';
import { Button, Card, Input, Spinner } from '../design-system';
import { toast } from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';

const CreatePracticeForm = ({ onSuccess, onCancel }) => {
  const { user, refreshUserInfo } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    practiceType: 'solo',
    email: '',
    phone: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await apiPost('/.netlify/functions/create-practice', {
        name: formData.name,
        description: formData.description || null,
        practiceType: formData.practiceType,
        email: formData.email || null,
        phone: formData.phone || null
      }, user);

      toast.success('Practice created successfully!');
      
      // Refresh user info to get practice data
      if (refreshUserInfo) {
        await refreshUserInfo();
      }

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.error('Error creating practice:', error);
      toast.error(error.message || 'Failed to create practice');
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: 'var(--color-background)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: 'var(--space-8) var(--space-4)'
    }}>
      <Card variant="default" padding="large" style={{ maxWidth: '600px', width: '100%' }}>
        {onCancel && (
          <Button
            variant="ghost"
            onClick={onCancel}
            style={{ marginBottom: 'var(--space-4)' }}
          >
            <ArrowLeft size={16} style={{ marginRight: 'var(--space-2)' }} />
            Back
          </Button>
        )}

        <h2 style={{ 
          fontSize: 'var(--text-h2)', 
          fontWeight: 'var(--weight-bold)', 
          color: 'var(--color-text)',
          marginBottom: 'var(--space-2)'
        }}>
          Create New Practice
        </h2>
        <p style={{ 
          fontSize: 'var(--text-body)', 
          color: 'var(--color-text)', 
          opacity: 0.7,
          marginBottom: 'var(--space-6)'
        }}>
          Set up your practice. You'll be the admin and can invite others later.
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div>
            <label style={{ 
              display: 'block', 
              fontSize: 'var(--text-caption)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--color-text)',
              marginBottom: 'var(--space-2)'
            }}>
              Practice Name *
            </label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Wellness Therapy Group"
              minLength={3}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              fontSize: 'var(--text-caption)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--color-text)',
              marginBottom: 'var(--space-2)'
            }}>
              Description (Optional)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description of your practice"
              style={{
                width: '100%',
                minHeight: '100px',
                padding: 'var(--space-3)',
                fontSize: 'var(--text-body)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              fontSize: 'var(--text-caption)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--color-text)',
              marginBottom: 'var(--space-2)'
            }}>
              Practice Type
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="practiceType"
                  value="solo"
                  checked={formData.practiceType === 'solo'}
                  onChange={handleChange}
                />
                <span>Solo Practice</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="practiceType"
                  value="group"
                  checked={formData.practiceType === 'group'}
                  onChange={handleChange}
                />
                <span>Group Practice</span>
              </label>
            </div>
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              fontSize: 'var(--text-caption)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--color-text)',
              marginBottom: 'var(--space-2)'
            }}>
              Contact Email (Optional)
            </label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="contact@practice.com"
            />
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              fontSize: 'var(--text-caption)', 
              fontWeight: 'var(--weight-semibold)', 
              color: 'var(--color-text)',
              marginBottom: 'var(--space-2)'
            }}>
              Phone (Optional)
            </label>
            <Input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1-555-0123"
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="large"
            disabled={isLoading || !formData.name.trim()}
            style={{ marginTop: 'var(--space-4)' }}
          >
            {isLoading ? (
              <>
                <Spinner size="small" />
                <span style={{ marginLeft: 'var(--space-2)' }}>Creating...</span>
              </>
            ) : (
              'Create Practice'
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default CreatePracticeForm;

